import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Text,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaRegComment } from "react-icons/fa";
import {
  MinimumRequirementResponseType,
  type MinimumRequirementDto,
} from "../../../../../autogen/bff-api";
import { SelectableCard } from "../../../../../common/input/SelectableCard";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";
import { MINIMUM_REQUIREMENT_RESPONSE_TYPE_OPTIONS } from "../utils/publicProcurementOptions";

// t('Add minimum requirement') t('Edit minimum requirement') t('Type') t('Title') t('Save')
// t('Minimum requirement') t('Minimum requirement title') t('Cancel') t('Type is required') t('Title is required')
// Response-type keys are resolved via variables, so pin them for i18next-parser:
// t('Yes / No') t('A simple yes or no answer.') t('Yes / No + comment')
// t('Yes or no, with room for an explanatory comment.') t('Respondent gets a comment field')

export interface MinimumRequirementFormValues {
  responseType: MinimumRequirementResponseType;
  title: string;
  description: string;
}

interface Props {
  isOpen: boolean;
  isSaving: boolean;
  mode: "add" | "edit";
  defaultValues?: MinimumRequirementFormValues;
  onSubmit: (values: Omit<MinimumRequirementDto, "id">) => Promise<void>;
  onClose: () => void;
}

const emptyDefaults: MinimumRequirementFormValues = {
  responseType: MinimumRequirementResponseType.YesNo,
  title: "",
  description: "",
};

export const MinimumRequirementDrawer = ({
  isOpen,
  isSaving,
  mode,
  defaultValues: defaultValuesProp,
  onSubmit,
  onClose,
}: Props) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<MinimumRequirementFormValues>({
    defaultValues: defaultValuesProp ?? emptyDefaults,
  });

  const submitHandler = handleSubmit(async (values) => {
    await onSubmit({
      title: values.title,
      description: values.description,
      responseType: values.responseType,
    });
  });

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} closeOnOverlayClick={!isSaving} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton isDisabled={isSaving} />
        <DrawerHeader>
          {t(mode === "edit" ? "Edit minimum requirement" : "Add minimum requirement")}
        </DrawerHeader>

        <DrawerBody>
          <form id="minimum-requirement-form" data-testid="minimum-requirement-form" onSubmit={submitHandler}>
            <FormControl mb="6" isRequired>
              <FormLabel>{t("Type")}</FormLabel>
              <Controller
                name="responseType"
                control={control}
                rules={{ required: t("Type is required") ?? "" }}
                render={({ field: { value, onChange } }) => (
                  <Box display="flex" flexDirection="column" gap="3">
                    {MINIMUM_REQUIREMENT_RESPONSE_TYPE_OPTIONS.map((option) => {
                      const selected = value === option.value;
                      return (
                        <SelectableCard
                          key={option.value}
                          isSelected={selected}
                          type="button"
                          w="full"
                          data-testid={`response-type-${option.value}`}
                          onClick={() => onChange(option.value)}
                        >
                          <Flex align="center" gap="3">
                            <Flex
                              align="center"
                              justify="center"
                              flexShrink={0}
                              boxSize="5"
                              borderRadius="full"
                              borderWidth="2px"
                              borderColor={selected ? "smPrimary" : "smBorder"}
                            >
                              {selected && <Box boxSize="2.5" borderRadius="full" bg="smPrimary" />}
                            </Flex>
                            <Box flex="1" minW="0">
                              <Text fontWeight="bold">{t(option.labelKey)}</Text>
                              <Text fontSize="sm" color="smMuted">
                                {t(option.descriptionKey)}
                              </Text>
                            </Box>
                            {option.hintKey && (
                              <Badge
                                display="flex"
                                alignItems="center"
                                gap="1.5"
                                bg="smPrimarySofter"
                                color="smPrimary"
                                px="2.5"
                                py="1"
                                borderRadius="md"
                                fontWeight="medium"
                                fontSize="xs"
                                textTransform="none"
                              >
                                <Icon as={FaRegComment} />
                                {t(option.hintKey)}
                              </Badge>
                            )}
                          </Flex>
                        </SelectableCard>
                      );
                    })}
                  </Box>
                )}
              />
              <FormErrorMessage>{errors.responseType?.message}</FormErrorMessage>
            </FormControl>

            <FormControl mb="6" isRequired isInvalid={errors.title !== undefined}>
              <FormLabel>{t("Title")}</FormLabel>
              <Input
                placeholder={t("Minimum requirement title") ?? ""}
                {...register("title", {
                  required: t("Title is required") ?? "",
                })}
              />
              <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
            </FormControl>

            <FormControl mb="6">
              <FormLabel>{t("Minimum requirement")}</FormLabel>
              <Controller
                name="description"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TipTap
                    content={value}
                    onUpdate={onChange}
                    editable={true}
                    toolbar={{ underline: false, headings: false, colors: false, link: false, table: false, image: false, template: false }}
                    debounceWait={0}
                  />
                )}
              />
            </FormControl>
          </form>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor="smBorder">
          <Flex gap="3">
            <Button variant="outline" onClick={onClose} isDisabled={isSaving}>
              {t("Cancel")}
            </Button>
            <Button type="submit" form="minimum-requirement-form" colorScheme="teal" isLoading={isSaving}>
              {t("Save")}
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
